// Group G6's own words (boards and tables): the pipeline columns, the aging line, the compare grid's attributes and
// actions, the approval roles, the configuration table's words, the permission matrix's capabilities and the rule
// sentences these components draw. Every entry quotes the line of his glossary or of the vendored design-system
// excerpts it comes from; labels.test.mjs checks each quote, its text and its status. Words the shared modules already
// carry (statuses, verbs, roles, access words, Workload/Expertise, Edit/View history) are imported, not repeated.

import { defineLabels, ROLE_LABELS, template } from "../labels";

const DC31 = "Pipeline columns are Sourced · Qualified · In HM Review · Recruiter screen · HM interview · Final Interview Loop · Offer (P-2).";

/** The seven pipeline columns (DC-31), in board order. */
export const G6_COLUMN_LABELS = /* @__PURE__ */ defineLabels({
  sourced: { text: "Sourced" },
  qualified: { text: "Qualified" },
  inHmReview: { text: "In HM Review" },
  recruiterScreen: { text: "Recruiter screen" },
  hmInterview: { text: "HM interview" },
  finalInterviewLoop: { text: "Final Interview Loop" },
  offer: { text: "Offer" },
});

const CMP = "Potential matches · Primary email · Secondary email · Name · Phone · LinkedIn URL · Candidate history · Use existing (\"use the existing profile\") · Create distinct · Merge";
const CMP_PREVIEW = "per-cell radios and a Preview column for merge";
const MASKED = "a security-trimmed row: --text-3 on --t-neutral, no title, no link, the words subject to permission";
const APPROVALS = "HM · HRBP · Organizational leader (obtained by HM) · Total Rewards · Pending · Approved · Rejected · Revision Required · Evidence";
const CFG = "configuration table: Effective version mono column, Current pill, Show previous versions toggle, per-row ibtn + menu (Edit · View history), page action New version";
const CFG_KINDS = "Statuses · Reason lists · Taxonomies · Thresholds · Reminders · Templates · Segments · Strategies · Effective version";
const QE16 = "Should the active version show Current and an old one Previous version (rather than Superseded)?";
const VERSIONS = "v1 · v2 · v3 · Current (Q-E16)";
const MATRIX = "Master permission matrix — the capabilities and the access words (BRD §13)";

/** The words of the compare grid, the approvals list, the configuration table and the permission matrix. */
export const G6_LABELS = /* @__PURE__ */ defineLabels({
  potentialMatches: { text: "Potential matches" },
  primaryEmail: { text: "Primary email" },
  secondaryEmail: { text: "Secondary email" },
  name: { text: "Name" },
  phone: { text: "Phone" },
  linkedinUrl: { text: "LinkedIn URL" },
  candidateHistory: { text: "Candidate history" },
  useExisting: { text: "Use existing" },
  createDistinct: { text: "Create distinct" },
  preview: { text: "Preview" },
  subjectToPermission: { text: "subject to permission" },
  organizationalLeader: { text: "Organizational leader" },
  totalRewards: { text: "Total Rewards" },
  evidence: { text: "Evidence" },
  effectiveVersion: { text: "Effective version" },
  newVersion: { text: "New version" },
  editViewHistory: { text: "Edit · View history" },
  current: { text: "Current" },
  previousVersion: { text: "Previous version" },
  statuses: { text: "Statuses" },
  reasonLists: { text: "Reason lists" },
  thresholds: { text: "Thresholds" },
  reminders: { text: "Reminders" },
  v1: { text: "v1" },
  v2: { text: "v2" },
  v3: { text: "v3" },
  permissionMatrix: { text: "Master permission matrix" },
  capabilities: { text: "capabilities" },
  reassignmentRequest: { text: "Reassignment request" },
});

/** The capability rows of the master permission matrix (G §16), each quoting its row. */
export const G6_CAPABILITY_LABELS = /* @__PURE__ */ defineLabels({
  createRequest: { text: "Create request" },
  certifyRequest: { text: "Certify request/JD" },
  createPostReq: { text: "Create/post req" },
  assignRecruiter: { text: "Assign/reassign Recruiter" },
  scheduleMeetings: { text: "Schedule meetings" },
  fullCompensationData: { text: "Full compensation data" },
  debriefDecision: { text: "Debrief decision" },
  holdCancelReopen: { text: "Hold/cancel/reopen" },
});

/**
 * His rule sentence with the acting role as a slot. The BRD wrote it for the 8 Oct TA Admin; DC-228 (as amended 12 Oct
 * 05:29) folds the TA Admin into the one Admin, and the glossary's Admin row carries the sentence, so the role is drawn
 * with the Admin word. His TA Manager sentences stay verbatim (the TA Manager is a role of its own).
 */
export const G6_RULE_TEMPLATES = {
  configures: /* @__PURE__ */ template({ text: "{role} may configure statuses, reason lists, taxonomies, thresholds, reminders, templates and segments." 


}),
} as const;

/** The rule sentences the boards draw as blocked reasons and refusals. */
export const G6_RULE_LABELS = Object.freeze({
  .../* @__PURE__ */ defineLabels({
    taManagerAssigns: { text: "TA Manager assigns based on workload and expertise." },
    reassignmentApproval: { text: "Recruiter/HRBP may request reassignment; TA Manager approves." },
    neverAutoMerge: { text: "Never auto-merge." },
  }),
  adminConfigures: G6_RULE_TEMPLATES.configures({ role: ROLE_LABELS.admin }),
});

export const G6_TEMPLATES = {
  /** The Kanban card's aging line (DC-97). */
  inStage: /* @__PURE__ */ template({ text: "In stage {n} days · Active Time" }),
  /** An approval obtained by another seat (DS row 116). */
  obtainedBy: /* @__PURE__ */ template({ text: "obtained by {role}" }),
} as const;
